import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Accordion, Input, Skeleton } from '@treams/treams-ui';

import FeedbackLibraryAccordionItems from './FeedbackLibraryAccordionItems';

type Props = {
	selectedQuestions: graphql.FeedbackQuestion[];
	onChangeSelectedQuestions: (questions: graphql.FeedbackQuestion[]) => void;
};

const FeedBackLibraryAccordion: FC<Props> = ({ selectedQuestions, onChangeSelectedQuestions }) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [searchQuery, setSearchQuery] = useState('');

	const query = graphql.useFeedbackLibraryQuery({
		fetchPolicy: 'network-only',
		variables: { isArchived: false },
	});

	const categories = query.data?.feedbackQuestionCategories.edges
		.map(({ node }) => node as graphql.FeedbackQuestionCategory)
		.sort((a, b) => {
			return localize(a.name).text.localeCompare(localize(b.name).text);
		});

	const questions = query.data?.feedbackQuestions.edges.map(
		({ node }) => node as graphql.FeedbackQuestion,
	);

	return (
		<>
			<Input
				mb={3}
				search
				icon={<SearchIcon />}
				placeholder={t('settings.feedbackLibrary.search.placeholder')}
				value={searchQuery}
				onChange={setSearchQuery}
			/>

			{categories ? (
				<Box>
					{categories?.map((category, index) => (
						<Accordion
							key={index}
							compact
							title={localize(category.name).text}
							isExpanded={searchQuery.length > 0}
						>
							<FeedbackLibraryAccordionItems
								searchQuery={searchQuery}
								questions={questions?.filter((q) => q.category.id === category.id) ?? []}
								selectedQuestions={selectedQuestions}
								onChangeSelectedQuestions={onChangeSelectedQuestions}
							/>
						</Accordion>
					))}
				</Box>
			) : (
				<Skeleton.Repeat count={6}>
					<Box mb={3}>
						<Accordion.Skeleton />
					</Box>
				</Skeleton.Repeat>
			)}
		</>
	);
};

export default FeedBackLibraryAccordion;
